<script setup lang="ts">
import { ActionsEnum, SubjectsEnum } from "@/services/ability";
import useLoginManager from "@/services/loginManager";
import { useI18n } from "vue-i18n";

import AvatarSVG from "@/assets/icons/avatar.svg";
import VortexLogoSVG from "@/assets/icons/favicon.svg";

const loginManager = useLoginManager();

const { t } = useI18n();

const isDev = import.meta.env.VITE_SENTRY_ENVIRONMENT == "dev";
</script>

<template>
  <v-bottom-navigation
    bg-color="headers"
    color="primary"
    horizontal
    class="tw:pos-fixed w-full tw:top-0 tw:z-10 tw:!px-4 bg-primary"
  >
    <!-- APP TITLE -->
    <div class="tw:flex tw:items-center tw:h-full tw:!px-4">
      <img
        :src="VortexLogoSVG"
        alt="Logo"
        class="tw:h-[50px] tw:w-[50px] tw:!mr-2"
      />
      <v-icon
        v-if="isDev"
        title="DEV"
      >
        mdi-bug
      </v-icon>
      <span class="text-h6">{{ t("app.title") }}</span>
    </div>

    <v-spacer />

    <!-- MENU TAB -->
    <v-btn to="/dashboard">
      <v-icon>mdi-file-tree</v-icon>
      <span>{{ t("menu.dashboard") }}</span>
    </v-btn>

    <v-divider
      class="mx-3"
      inset
      vertical
    />

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.CHILD"
    >
      <v-btn to="/children">
        <v-icon>mdi-baby-carriage</v-icon>
        <span>{{ t("menu.children") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.FAMILY"
    >
      <v-btn to="/families">
        <v-icon>mdi-human-male-female-child</v-icon>
        <span>{{ t("menu.families") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.USER"
    >
      <v-btn to="/users">
        <v-icon>mdi-human-male-female</v-icon>
        <span>{{ t("menu.users") }}</span>
      </v-btn>
    </Can>

    <v-divider
      class="mx-3"
      inset
      vertical
    />

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.REGION"
    >
      <v-btn to="/regions">
        <v-icon>mdi-map</v-icon>
        <span>{{ t("menu.regions") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.DEALER"
    >
      <v-btn to="/dealers">
        <v-icon>mdi-account-tie</v-icon>
        <span>{{ t("menu.dealers") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.DEVICE"
    >
      <v-btn to="/devices">
        <v-icon>mdi-devices</v-icon>
        <span>{{ t("menu.devices") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.LICENSE"
    >
      <v-btn to="/licenses">
        <v-icon>mdi-receipt-text-clock</v-icon>
        <span>{{ t("menu.licenses") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.SERVICE"
    >
      <v-btn to="/services">
        <v-icon>mdi-invoice-list</v-icon>
        <span>{{ t("menu.services") }}</span>
      </v-btn>
    </Can>

    <Can
      :do="ActionsEnum.USE"
      :on="SubjectsEnum.SYSTEM"
    >
      <v-divider
        class="mx-3"
        inset
        vertical
      />

      <v-btn>
        <v-icon>mdi-security</v-icon>
        <span>{{ t("menu.system") }}</span>
        <v-menu activator="parent">
          <v-list>
            <v-list-item to="/service-types">
              {{ t("menu.serviceTypes") }}
            </v-list-item>

            <v-list-item to="/roles">
              {{ t("menu.roles") }}
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn>
    </Can>
    <v-spacer />

    <!-- NOTIFY -->
    <!-- <NotificationBell class="mx-3" /> -->

    <!-- USER -->
    <v-btn
      icon
      class="tw:!max-w-none no-wrap"
    >
      <v-list-item
        :title="loginManager.userInfo?.value?.full_name"
        color=""
        density="comfortable"
        :nav="true"
        class="text-left"
      >
        <template #prepend>
          <v-avatar
            color="surface-light"
            size="32"
            class="tw:-mr-2"
          >
            <v-img :src="AvatarSVG" />
          </v-avatar>
        </template>
        <template #subtitle>
          <span class="font-weight-medium">
            {{ loginManager.userInfo?.value?.email }}
          </span>
        </template>
      </v-list-item>

      <!-- USER MENU -->
      <v-menu activator="parent">
        <v-list>
          <v-list-item
            prepend-icon="mdi-cog"
            to="/settings"
          >
            {{ t("menu.settings") }}
          </v-list-item>

          <v-list-item
            prepend-icon="mdi-logout"
            to="/logout"
            @click="loginManager.logout"
          >
            {{ t("menu.logout") }}
          </v-list-item>
        </v-list>
      </v-menu>
    </v-btn>
  </v-bottom-navigation>
</template>

<style lang="scss" scoped>
.v-bottom-navigation {
  :deep(.v-bottom-navigation__content) {
    max-width: 130rem;
    margin: 0 auto;
  }
}
</style>
